import { Check, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { getCheckoutAttribution, trackEvent } from "./analytics";
import { openCheckout, type ProductOffer } from "./api";
import "./purchase.css";

export function PurchaseModal({
  open,
  language,
  products,
  paymentEnabled,
  onClose,
  onRefresh,
}: {
  open: boolean;
  language: "zh" | "en";
  products: ProductOffer[];
  paymentEnabled: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}) {
  const [busySku, setBusySku] = useState<ProductOffer["sku"] | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const onFocus = () => {
      void onRefresh();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("focus", onFocus);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("focus", onFocus);
    };
  }, [onClose, onRefresh, open]);

  if (!open) return null;
  const copy =
    language === "zh"
      ? {
          eyebrow: "继续完成这次取名",
          title: "选择取名次数",
          intro: "一次生成 5 个完整姓名方案。只按次购买，不订阅，未使用次数不会过期。",
          recommended: "推荐 · 平均 ¥8.90/次",
          single: "单次体验",
          ten: "十次组合",
          buy: "前往安全收银台",
          later: "稍后再说",
          disabled: "正式支付正在准备中",
          opened: "收银台已在新窗口打开。支付完成后回到这里，次数会自动刷新。",
        }
      : {
          eyebrow: "CONTINUE YOUR NAME JOURNEY",
          title: "Choose a generation pack",
          intro:
            "Each generation delivers five complete Chinese names. One-time payment, no subscription, and unused credits do not expire.",
          recommended: "BEST VALUE · $4.00 EACH",
          single: "Single generation",
          ten: "Ten generations",
          buy: "Open secure checkout",
          later: "Maybe later",
          disabled: "Live payments are being prepared",
          opened:
            "Checkout opened in a new window. Return here after payment and your credits will refresh.",
        };

  async function buy(product: ProductOffer) {
    setBusySku(product.sku);
    setMessage("");
    try {
      trackEvent("checkout_started", {
        sku: product.sku,
        value: product.amountMinor / 100,
        currency: product.currency.toUpperCase(),
        credits: product.credits,
      });
      await openCheckout(product.sku, getCheckoutAttribution());
      setMessage(copy.opened);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Checkout unavailable");
    } finally {
      setBusySku(null);
    }
  }

  return (
    <div className="purchase-backdrop">
      <section
        aria-labelledby="purchase-title"
        aria-modal="true"
        className={`purchase-dialog ${language}`}
        role="dialog"
      >
        <button aria-label={copy.later} className="purchase-close" type="button" onClick={onClose}>
          <X />
        </button>
        <span className="purchase-eyebrow">{copy.eyebrow}</span>
        <h2 id="purchase-title">{copy.title}</h2>
        <p className="purchase-intro">{copy.intro}</p>
        <div className="purchase-options">
          {[...products]
            .sort((a, b) => b.credits - a.credits)
            .map((product) => {
              const recommended = product.credits === 10;
              const symbol = product.currency === "cny" ? "¥" : "$";
              return (
                <article
                  className={recommended ? "purchase-option recommended" : "purchase-option"}
                  key={product.sku}
                >
                  {recommended ? (
                    <span className="purchase-badge">
                      <Sparkles /> {copy.recommended}
                    </span>
                  ) : null}
                  <span className="purchase-plan">{recommended ? copy.ten : copy.single}</span>
                  <div className="purchase-price">
                    <strong>
                      {symbol}
                      {product.displayAmount}
                    </strong>
                    <small>/{product.credits === 10 ? "10" : "1"}</small>
                  </div>
                  <ul>
                    <li>
                      <Check /> {product.credits}{" "}
                      {language === "zh" ? "次取名" : "generation credits"}
                    </li>
                    <li>
                      <Check />{" "}
                      {language === "zh" ? "每次 5 个完整方案" : "Five complete names each"}
                    </li>
                  </ul>
                  <button
                    className="purchase-buy"
                    disabled={!paymentEnabled || busySku !== null}
                    type="button"
                    onClick={() => void buy(product)}
                  >
                    {paymentEnabled ? (busySku === product.sku ? "…" : copy.buy) : copy.disabled}
                  </button>
                </article>
              );
            })}
        </div>
        {message ? <p className="purchase-message">{message}</p> : null}
        <button className="purchase-later" type="button" onClick={onClose}>
          {copy.later}
        </button>
      </section>
    </div>
  );
}
